import {
	useRef,
	useEffect,
	useCallback,
	useState
} from 'react';

import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import { toPng } from 'html-to-image';
import Plotly from 'plotly.js-dist-min';

import Snackbar from '../common/Snackbar';

import { handleExport } from '../../utils/exportUtils';
import { API_BASE_FILES } from '../../utils/constants';
import { formatMessageText, sanitizeFilename, timestamp } from '@/utils/formatters';

import { useTour } from '@reactour/tour';

import { ChartRenderer } from '@/components/charts';

import { EXPORT_INLINE_STYLE_PROPERTIES } from './chat.config';

/* =========================================================
   Shared Markdown Renderer
========================================================= */

const normalizeMathDelimiters = (text) => {
	if (!text) return '';

	return String(text)
		.replace(/\\\((.*?)\\\)/gs, (_, formula) => `$${formula}$`)
		.replace(/\\\[(.*?)\\\]/gs, (_, formula) => `$$${formula}$$`)
		.replace(/\\frac/g, '\\dfrac');
};

const toPersianDigits = (value) =>
	String(value ?? '').replace(
		/\d/g,
		(digit) => '۰۱۲۳۴۵۶۷۸۹'[Number(digit)]
	);

/*
 * KaTeX renders its own HTML, so formatMessageText cannot localize the
 * numbers inside formulas. This rehype plugin runs AFTER rehype-katex and
 * only converts the visible KaTeX HTML digits to Persian digits.
 *
 * We intentionally leave .katex-mathml untouched so the semantic MathML
 * representation remains valid for accessibility/copying.
 */
const rehypePersianKatexDigits = () => (tree) => {
	const walk = (
		node,
		insideKatexHtml = false,
		insideKatexMathml = false
	) => {
		if (!node) {
			return;
		}

		let nextInsideKatexHtml = insideKatexHtml;
		let nextInsideKatexMathml = insideKatexMathml;

		if (node.type === 'element') {
			const rawClassName = node.properties?.className;

			const classNames = Array.isArray(rawClassName)
				? rawClassName
				: typeof rawClassName === 'string'
					? rawClassName.split(/\s+/)
					: [];

			if (classNames.includes('katex-html')) {
				nextInsideKatexHtml = true;
			}

			if (classNames.includes('katex-mathml')) {
				nextInsideKatexMathml = true;
			}
		}

		if (
			node.type === 'text' &&
			insideKatexHtml &&
			!insideKatexMathml
		) {
			node.value = toPersianDigits(node.value);
		}

		if (Array.isArray(node.children)) {
			node.children.forEach((child) => {
				walk(
					child,
					nextInsideKatexHtml,
					nextInsideKatexMathml
				);
			});
		}
	};

	walk(tree);
};

const safeJsonStringify = (value) => {
	try {
		return JSON.stringify(value, null, 2);
	} catch (error) {
		console.error(
			'Failed to stringify structured output:',
			error
		);

		return String(value ?? '');
	}
};

const buildStructuredOutputText = ({
	content,
	report,
	charts
}) => {
	const validCharts = Array.isArray(charts)
		? charts.filter(Boolean)
		: [];

	const hasReport =
		report !== null &&
		typeof report === 'object' &&
		!Array.isArray(report) &&
		Object.keys(report).length > 0;

	if (!hasReport && validCharts.length === 0) {
		return String(content || '');
	}

	const parts = [];

	if (hasReport) {
		if (report.title) {
			parts.push(`# ${report.title}`);
		}

		if (report.executive_summary) {
			parts.push(String(report.executive_summary));
		}
	} else if (content) {
		parts.push(String(content));
	}

	const primaryFinding =
		hasReport &&
		report.primary_finding &&
		typeof report.primary_finding === 'object'
			? report.primary_finding
			: null;

	const supportingFindings =
		hasReport &&
		Array.isArray(report.supporting_findings)
			? report.supporting_findings.filter(Boolean)
			: [];

	const allFindings = [
		primaryFinding,
		...supportingFindings
	].filter(Boolean);

	const findingsByChartId = allFindings.reduce(
		(result, finding) => {
			if (!finding?.chart_id) {
				return result;
			}

			if (!result[finding.chart_id]) {
				result[finding.chart_id] = [];
			}

			result[finding.chart_id].push(finding);
			return result;
		},
		{}
	);

	const renderedChartIds = new Set(
		validCharts
			.map((chart) => chart?.chart_id)
			.filter(Boolean)
	);

	const appendFinding = (finding) => {
		if (!finding) {
			return;
		}

		if (finding.title) {
			parts.push(`## ${finding.title}`);
		}

		if (finding.summary) {
			parts.push(String(finding.summary));
		}
	};

	/*
	 * For text-only copy/export, keep the human-readable report text
	 * but DO NOT dump chart JSON/payloads into the result.
	 */
	validCharts.forEach((chart) => {
		const relatedFindings =
			findingsByChartId[chart?.chart_id] || [];

		relatedFindings.forEach(appendFinding);
	});

	allFindings
		.filter(
			(finding) =>
				!finding?.chart_id ||
				!renderedChartIds.has(finding.chart_id)
		)
		.forEach(appendFinding);

	const additionalObservations =
		hasReport &&
		Array.isArray(report.additional_observations)
			? report.additional_observations.filter(Boolean)
			: [];

	if (additionalObservations.length > 0) {
		parts.push('## مشاهدات تکمیلی');

		additionalObservations.forEach(
			(observation) => {
				const normalized =
					normalizeObservation(observation);

				if (!normalized.summary) {
					return;
				}

				if (normalized.title) {
					parts.push(
						`### ${normalized.title}`
					);
				}

				parts.push(String(normalized.summary));
			}
		);
	}

	if (hasReport && report.overall_conclusion) {
		parts.push('## جمع‌بندی نهایی');
		parts.push(String(report.overall_conclusion));
	}

	/*
	 * Some structured responses still contain useful normal content.
	 * Add it only if the report itself did not provide any readable text.
	 */
	if (parts.length === 0 && content) {
		parts.push(String(content));
	}

	return parts
		.filter(
			(part) =>
				part !== null &&
				part !== undefined &&
				String(part).trim() !== ''
		)
		.join('\n\n');
};

const inlineComputedStyles = (
	sourceRoot,
	cloneRoot
) => {
	if (!sourceRoot || !cloneRoot) {
		return;
	}

	const sourceElements = [
		sourceRoot,
		...sourceRoot.querySelectorAll('*')
	];

	const cloneElements = [
		cloneRoot,
		...cloneRoot.querySelectorAll('*')
	];

	sourceElements.forEach(
		(sourceElement, elementIndex) => {
			const cloneElement =
				cloneElements[elementIndex];

			if (!cloneElement) {
				return;
			}

			const computedStyle =
				window.getComputedStyle(sourceElement);

			EXPORT_INLINE_STYLE_PROPERTIES.forEach(
				(property) => {
					const value =
						computedStyle.getPropertyValue(
							property
						);

					if (value) {
						cloneElement.style.setProperty(
							property,
							value
						);
					}
				}
			);
		}
	);
};

const hidePlotlyModeBars = (chartElement) => {
	const modeBars = Array.from(
		chartElement.querySelectorAll(
			'.modebar-container, .modebar'
		)
	);

	const previousStyles = modeBars.map(
		(element) => ({
			element,
			display: element.style.display,
			visibility: element.style.visibility
		})
	);

	modeBars.forEach((element) => {
		element.style.display = 'none';
		element.style.visibility = 'hidden';
	});

	return () => {
		previousStyles.forEach(
			({ element, display, visibility }) => {
				element.style.display = display;
				element.style.visibility = visibility;
			}
		);
	};
};

const capturePlotlyChart = async (
	chartElement
) => {
	const plotlyElement =
		chartElement.matches?.(
			'.js-plotly-plot'
		)
			? chartElement
			: chartElement.querySelector(
					'.js-plotly-plot'
			  );

	if (!plotlyElement) {
		return null;
	}

	const rect =
		plotlyElement.getBoundingClientRect();

	const width = Math.max(
		1,
		Math.round(
			rect.width ||
				plotlyElement.clientWidth ||
				800
		)
	);

	const height = Math.max(
		1,
		Math.round(
			rect.height ||
				plotlyElement.clientHeight ||
				500
		)
	);

	/*
	 * Plotly owns the SVG/canvas/WebGL/map rendering, so its own exporter
	 * is much more reliable than taking a DOM screenshot. Modebar controls
	 * are not part of the generated static chart image.
	 */
	return Plotly.toImage(
		plotlyElement,
		{
			format: 'png',
			width,
			height,
			scale: 1
		}
	);
};

const captureChartImages = async (
	structuredReportElement
) => {
	if (!structuredReportElement) {
		return [];
	}

	const chartElements = Array.from(
		structuredReportElement.querySelectorAll(
			'.report-chart-container'
		)
	);

	const captures = [];

	/*
	 * Capture sequentially to avoid putting several Plotly/WebGL exports
	 * under pressure at the same time.
	 */
	for (
		let chartIndex = 0;
		chartIndex < chartElements.length;
		chartIndex += 1
	) {
		const chartElement =
			chartElements[chartIndex];

		let dataUrl = null;

		/*
		 * Preferred path for Plotly charts, including scattermap / choroplethmap.
		 */
		try {
			dataUrl = await capturePlotlyChart(
				chartElement
			);
		} catch (error) {
			console.warn(
				`Plotly image export failed for chart ${chartIndex + 1}; trying DOM fallback:`,
				error
			);
		}

		/*
		 * Fallback for a non-Plotly chart (or a Plotly export failure).
		 * Hide Plotly modebar controls so they are never baked into the PNG.
		 */
		if (!dataUrl) {
			const restoreModeBars =
				hidePlotlyModeBars(
					chartElement
				);

			try {
				dataUrl = await toPng(
					chartElement,
					{
						cacheBust: true,
						pixelRatio: Math.min(
							window.devicePixelRatio || 1,
							2
						)
					}
				);
			} catch (error) {
				console.error(
					`Failed to capture chart ${chartIndex + 1}:`,
					error
				);
			} finally {
				restoreModeBars();
			}
		}

		if (dataUrl) {
			captures.push({
				index: chartIndex,
				dataUrl
			});
		}
	}

	return captures;
};

const loadImage = (src) =>
	new Promise((resolve, reject) => {
		const image = new Image();

		image.onload = () => resolve(image);
		image.onerror = reject;
		image.src = src;
	});

const combineChartCapturesToBlob = async (
	chartCaptures
) => {
	if (!Array.isArray(chartCaptures) || chartCaptures.length === 0) {
		return null;
	}

	const images = [];

	for (const capture of chartCaptures) {
		try {
			const image = await loadImage(
				capture.dataUrl
			);

			images.push(image);
		} catch (error) {
			console.error(
				'Failed to load captured chart image:',
				error
			);
		}
	}

	if (images.length === 0) {
		return null;
	}

	const gap = 24;
	const naturalWidth = Math.max(
		...images.map((image) => image.width)
	);

	const naturalHeight =
		images.reduce(
			(total, image) => total + image.height,
			0
		) +
		gap * Math.max(images.length - 1, 0);

	/*
	 * Keep the combined clipboard PNG under conservative browser canvas
	 * limits so very long multi-chart reports still have a chance to copy.
	 */
	const scale = Math.min(
		1,
		2400 / naturalWidth,
		15000 / naturalHeight
	);

	const canvas = document.createElement('canvas');
	canvas.width = Math.max(
		1,
		Math.round(naturalWidth * scale)
	);
	canvas.height = Math.max(
		1,
		Math.round(naturalHeight * scale)
	);

	const context = canvas.getContext('2d');

	if (!context) {
		return null;
	}

	context.fillStyle = '#ffffff';
	context.fillRect(
		0,
		0,
		canvas.width,
		canvas.height
	);

	let currentY = 0;

	images.forEach((image) => {
		const width = image.width * scale;
		const height = image.height * scale;

		context.drawImage(
			image,
			0,
			currentY,
			width,
			height
		);

		currentY += height + gap * scale;
	});

	return new Promise((resolve) => {
		canvas.toBlob(
			(blob) => resolve(blob),
			'image/png'
		);
	});
};

const buildStructuredHtmlWithImages = ({
	structuredReportElement,
	chartCaptures
}) => {
	if (!structuredReportElement) {
		return '';
	}

	const clone =
		structuredReportElement.cloneNode(true);

	/* Preserve the current visual typography/layout as much as possible. */
	inlineComputedStyles(
		structuredReportElement,
		clone
	);

	const clonedChartContainers = Array.from(
		clone.querySelectorAll(
			'.report-chart-container'
		)
	);

	const capturesByIndex = new Map(
		(chartCaptures || []).map(
			(capture) => [capture.index, capture]
		)
	);

	clonedChartContainers.forEach(
		(chartContainer, chartIndex) => {
			const capture =
				capturesByIndex.get(chartIndex);

			const chartSection =
				chartContainer.closest(
					'.report-chart-section'
				);

			/*
			 * inlineComputedStyles copied the live section height. Once the
			 * interactive Plotly DOM is replaced/removed, let export HTML size
			 * itself naturally so a failed chart does not leave a large gap.
			 */
			if (chartSection) {
				chartSection.style.height = 'auto';
				chartSection.style.minHeight = '0';
			}

			/*
			 * If this chart could not be captured, remove only the visual chart.
			 * Findings that are siblings inside the section stay in the export.
			 */
			if (!capture) {
				chartContainer.remove();
				return;
			}

			chartContainer.innerHTML = '';
			chartContainer.style.height = 'auto';
			chartContainer.style.minHeight = '0';

			const image = document.createElement('img');
			image.src = capture.dataUrl;
			image.alt = `Chart ${chartIndex + 1}`;
			image.style.display = 'block';
			image.style.width = '100%';
			image.style.maxWidth = '100%';
			image.style.height = 'auto';
			image.style.margin = '12px 0';

			chartContainer.appendChild(image);
		}
	);

	return clone.outerHTML;
};

const escapeHtml = (value) =>
	String(value ?? '')
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#039;');

const wrapExportHtmlDocument = ({
	bodyHtml,
	title = 'Assistant response'
}) => `<!doctype html>
<html lang="fa" dir="rtl">
<head>
	<meta charset="utf-8" />
	<meta name="viewport" content="width=device-width, initial-scale=1" />
	<title>${String(title).replace(/[<>]/g, '')}</title>
	<style>
		body {
			margin: 32px auto;
			max-width: 1100px;
			padding: 0 24px;
			font-family: Arial, Tahoma, sans-serif;
			line-height: 1.8;
			direction: rtl;
		}
		img { max-width: 100%; height: auto; }
		table { border-collapse: collapse; width: 100%; }
		th, td { border: 1px solid #ddd; padding: 8px; }
		pre, code { direction: ltr; text-align: left; white-space: pre-wrap; }
	</style>
</head>
<body>
${bodyHtml}
</body>
</html>`;

const downloadBlob = (
	blob,
	filename
) => {
	const objectUrl = URL.createObjectURL(blob);
	const link = document.createElement('a');

	link.href = objectUrl;
	link.download = filename;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);

	window.setTimeout(() => {
		URL.revokeObjectURL(objectUrl);
	}, 1000);
};

const markdownComponents = {
	span: ({
		className,
		children,
		...props
	}) => {
		const isKatexDisplay =
			className?.includes('katex-display');

		const isKatex =
			!isKatexDisplay &&
			className?.split(/\s+/).includes('katex');

		let style;

		/*
		 * Inline fractions and other tall formulas need some breathing room
		 * in Persian text. Making the KaTeX root inline-block also prevents
		 * the fraction from visually touching the lines above/below it.
		 */
		if (isKatex) {
			style = {
				display: 'inline-block',
				margin: '0.35em 0.3em',
				padding: '0.08em 0',
				verticalAlign: 'middle'
			};
		} else if (isKatexDisplay) {
			style = {
				display: 'block',
				margin: '1rem 0',
				padding: '0.35rem 0',
				overflowX: 'auto',
				overflowY: 'hidden',
				textAlign: 'center'
			};
		}

		return (
			<span
				className={className}
				dir={
					className?.includes('katex')
						? 'ltr'
						: undefined
				}
				style={style}
				{...props}
			>
				{children}
			</span>
		);
	},

	pre: ({ children }) => (
		<>
			<br />
			{children}
		</>
	),

	code: ({
		inline,
		className,
		children,
		...props
	}) => {
		if (!inline) {
			return (
				<code
					className={className}
					{...props}
					style={{
						display: 'block',
						whiteSpace: 'pre-wrap',
						overflowWrap: 'anywhere',
						overflowX: 'auto',
						padding: '1rem',
						backgroundColor: '#f5f5f5',
						borderRadius: '4px'
					}}
				>
					{children}
				</code>
			);
		}

		return (
			<code
				className={className}
				{...props}
			>
				{children}
			</code>
		);
	}
};

const MarkdownContent = ({ children }) => {
	if (
		children === null ||
		children === undefined ||
		children === ''
	) {
		return null;
	}

	return (
		<ReactMarkdown
			remarkPlugins={[
				remarkGfm,
				remarkMath
			]}
			rehypePlugins={[
				rehypeKatex,
				rehypePersianKatexDigits
			]}
			components={markdownComponents}
		>
			{normalizeMathDelimiters(children)}
		</ReactMarkdown>
	);
};

MarkdownContent.propTypes = {
	children: PropTypes.node
};

/* =========================================================
   Finding Renderer
========================================================= */

const FindingSection = ({ finding }) => {
	if (!finding) {
		return null;
	}

	return (
		<section
			className="report-finding"
			data-section-id={
				finding.section_id || undefined
			}
		>
			{finding.title && (
				<h3 className="report-finding-title">
					{finding.title}
				</h3>
			)}

			{finding.summary && (
				<div className="report-finding-summary">
					<MarkdownContent>
						{finding.summary}
					</MarkdownContent>
				</div>
			)}
		</section>
	);
};

FindingSection.propTypes = {
	finding: PropTypes.shape({
		section_id: PropTypes.string,
		title: PropTypes.string,
		summary: PropTypes.string,
		chart_id: PropTypes.string,
		insight_ids: PropTypes.arrayOf(
			PropTypes.string
		)
	})
};

/* =========================================================
   Additional Observation Normalizer
========================================================= */

const normalizeObservation = (observation) => {
	if (typeof observation === 'string') {
		return {
			title: '',
			summary: observation
		};
	}

	if (
		!observation ||
		typeof observation !== 'object'
	) {
		return {
			title: '',
			summary: ''
		};
	}

	return {
		title: observation.title || '',

		summary:
			observation.summary ||
			observation.description ||
			observation.text ||
			''
	};
};

/* =========================================================
   Structured Report Renderer
========================================================= */

const StructuredReport = ({
	report,
	charts,
	fallbackContent,
	geoJsonBaseUrl = '/iran-geojson'
}) => {
	const validCharts = Array.isArray(charts)
		? charts.filter(Boolean)
		: [];

	const hasReport =
		report !== null &&
		typeof report === 'object' &&
		!Array.isArray(report) &&
		Object.keys(report).length > 0;

	if (!hasReport && validCharts.length === 0) {
		return null;
	}

	const primaryFinding =
		hasReport &&
		report.primary_finding &&
		typeof report.primary_finding === 'object'
			? report.primary_finding
			: null;

	const supportingFindings =
		hasReport &&
		Array.isArray(report.supporting_findings)
			? report.supporting_findings.filter(Boolean)
			: [];

	const allFindings = [
		primaryFinding,
		...supportingFindings
	].filter(Boolean);

	/*
	 * Create:
	 *
	 * {
	 *   chart_1: [primary finding],
	 *   chart_2: [supporting finding]
	 * }
	 */
	const findingsByChartId = allFindings.reduce(
		(result, finding) => {
			if (!finding.chart_id) {
				return result;
			}

			if (!result[finding.chart_id]) {
				result[finding.chart_id] = [];
			}

			result[finding.chart_id].push(finding);

			return result;
		},
		{}
	);

	const renderedChartIds = new Set(
		validCharts
			.map((chart) => chart?.chart_id)
			.filter(Boolean)
	);

	/*
	 * A finding may reference a chart that was not returned.
	 * Do not lose it; render it after available charts.
	 */
	const unlinkedFindings = allFindings.filter(
		(finding) =>
			!finding.chart_id ||
			!renderedChartIds.has(finding.chart_id)
	);

	const additionalObservations =
		hasReport &&
		Array.isArray(report.additional_observations)
			? report.additional_observations.filter(Boolean)
			: [];

	return (
		<article
			className="structured-report"
			dir="rtl"
		>
			{/* 1. Report title */}
			{hasReport && (
				<header className="structured-report-header">
					{report.title && (
						<h2 className="structured-report-title">
							{report.title}
						</h2>
					)}

					{/* 2. Executive summary */}
					{report.executive_summary && (
						<section className="report-executive-summary">
							<MarkdownContent>
								{report.executive_summary}
							</MarkdownContent>
						</section>
					)}
				</header>
			)}

			{/*
			 * When charts exist but report does not,
			 * preserve normal assistant content before charts.
			 */}
			{!hasReport && fallbackContent && (
				<div className="report-fallback-content">
					<MarkdownContent>
						{fallbackContent}
					</MarkdownContent>
				</div>
			)}

			{/*
			 * 3. Render every chart in server order.
			 * 4. Render its associated finding immediately after it.
			 */}
			{validCharts.map((chart, chartIndex) => {
				const chartId =
					chart.chart_id ||
					`chart-${chartIndex}`;

				const relatedFindings =
					findingsByChartId[chart.chart_id] || [];

				return (
					<section
						key={chartId}
						className="report-chart-section"
						data-chart-id={
							chart.chart_id || undefined
						}
					>
						<div className="report-chart-container">
							{/* <ChartRenderer
								chart={chart}
								geoJsonBaseUrl={geoJsonBaseUrl}
								height={460}
								onError={(error) => {
									console.error(
										`Failed to render chart "${chartId}":`,
										error
									);
								}}
							/> */}
							<ChartRenderer
								chart={chart}
								height={500}
								onError={(error) => {
									console.error(
										`Failed to render chart "${chart.chart_id}":`,
										error
									);
								}}
							/>
						</div>

						{relatedFindings.map(
							(finding, findingIndex) => (
								<FindingSection
									key={
										finding.section_id ||
										`${chartId}-finding-${findingIndex}`
									}
									finding={finding}
								/>
							)
						)}
					</section>
				);
			})}

			{/*
			 * Render findings whose chart is missing.
			 */}
			{unlinkedFindings.length > 0 && (
				<section className="report-unlinked-findings">
					{unlinkedFindings.map(
						(finding, findingIndex) => (
							<FindingSection
								key={
									finding.section_id ||
									`unlinked-finding-${findingIndex}`
								}
								finding={finding}
							/>
						)
					)}
				</section>
			)}

			{/* 5. Additional observations */}
			{additionalObservations.length > 0 && (
				<section className="report-additional-observations">
					<h3 className="report-section-title">
						مشاهدات تکمیلی
					</h3>

					<div className="report-observations-list">
						{additionalObservations.map(
							(observation, observationIndex) => {
								const normalized =
									normalizeObservation(observation);

								if (!normalized.summary) {
									return null;
								}

								return (
									<div
										key={
											observation?.section_id ||
											`observation-${observationIndex}`
										}
										className="report-observation"
									>
										{normalized.title && (
											<h4>
												{normalized.title}
											</h4>
										)}

										<MarkdownContent>
											{normalized.summary}
										</MarkdownContent>
									</div>
								);
							}
						)}
					</div>
				</section>
			)}

			{/* 6. Overall conclusion */}
			{hasReport && report.overall_conclusion && (
				<section className="report-overall-conclusion">
					<h3 className="report-section-title">
						جمع‌بندی نهایی
					</h3>

					<MarkdownContent>
						{report.overall_conclusion}
					</MarkdownContent>
				</section>
			)}
		</article>
	);
};

StructuredReport.propTypes = {
	report: PropTypes.object,

	charts: PropTypes.arrayOf(
		PropTypes.object
	),

	fallbackContent: PropTypes.string,

	geoJsonBaseUrl: PropTypes.string
};

/* =========================================================
   Main Message Component
========================================================= */

const Message = ({
	msg,
	index,
	onEdit,
	onFeedback,
	exportMenuOpen,
	onToggleExportMenu,
	exportMenuStyle,
	messages,
	chatIds,
	currentChatIndex,
	isEditing,
	editingText,
	setEditingText,
	saveEdit,
	editingIndex
}) => {
	const formattedContent = formatMessageText(
		msg.content || ''
	);

	/*
	 * Supports both possible message structures:
	 *
	 * msg.report / msg.charts
	 *
	 * or:
	 *
	 * msg.result.report / msg.result.charts
	 */
	const report =
		msg.report ??
		msg.result?.report ??
		null;

	const chartsCandidate =
		msg.charts ??
		msg.result?.charts ??
		[];

	const charts = Array.isArray(chartsCandidate)
		? chartsCandidate.filter(Boolean)
		: [];

	const hasReport =
		report !== null &&
		typeof report === 'object' &&
		!Array.isArray(report) &&
		Object.keys(report).length > 0;

	const hasCharts = charts.length > 0;

	/*
	 * Important:
	 *
	 * If neither charts nor report exists,
	 * original ReactMarkdown rendering is used.
	 */
	const hasStructuredOutput =
		msg.role === 'assistant' &&
		(hasReport || hasCharts);

	const exportMenuRef = useRef(null);
	const messageElementRef = useRef(null);
	const editTextareaRef = useRef(null);

	const adjustExportMenuPosition = useCallback(() => {
		if (!exportMenuRef.current) {
			return;
		}

		const exportMenu = exportMenuRef.current;
		const chatBox =
			document.querySelector('.chat-box');

		if (!chatBox) {
			return;
		}

		const menuRect =
			exportMenu.getBoundingClientRect();

		const chatBoxRect =
			chatBox.getBoundingClientRect();

		const spaceBelow =
			chatBoxRect.bottom -
			(menuRect.top + menuRect.height);

		let topPosition = -224;

		if (spaceBelow < 0) {
			topPosition =
				-224 + spaceBelow - 10;
		}

		const spaceAbove =
			menuRect.top - chatBoxRect.top;

		if (spaceAbove < 0) {
			topPosition = 10;
		}

		exportMenu.style.top =
			`${topPosition}px`;
	}, []);

	useEffect(() => {
		if (!exportMenuOpen) {
			return undefined;
		}

		adjustExportMenuPosition();

		window.addEventListener(
			'resize',
			adjustExportMenuPosition
		);

		return () => {
			window.removeEventListener(
				'resize',
				adjustExportMenuPosition
			);
		};
	}, [
		exportMenuOpen,
		adjustExportMenuPosition
	]);

	useEffect(() => {
		const element = editTextareaRef.current;

		if (element) {
			const length = element.value.length;

			element.focus();
			element.setSelectionRange(length, length);
		}
	}, [editingIndex]);

	const getCopyableContent = () =>
		hasStructuredOutput
			? buildStructuredOutputText({
					content: msg.content,
					report,
					charts
			  })
			: String(msg.content || '');

	const handleExportClick = async (format) => {
		const textContent = getCopyableContent();

		/*
		 * HTML and Word-compatible DOC can carry chart screenshots.
		 * We first try to capture each rendered chart as PNG.
		 * Any chart that cannot be captured is simply removed, leaving
		 * the readable report text as the fallback.
		 */
		if (
			hasStructuredOutput &&
			(format === 'html' || format === 'doc')
		) {
			try {
				const structuredReportElement =
					messageElementRef.current?.querySelector(
						'.structured-report'
					);

				let bodyHtml = '';

				if (structuredReportElement) {
					const chartCaptures = hasCharts
						? await captureChartImages(
								structuredReportElement
						  )
						: [];

					bodyHtml = buildStructuredHtmlWithImages({
						structuredReportElement,
						chartCaptures
					});
				}

				/*
				 * DOM export may be unavailable in an unusual render state.
				 * In that case produce text only; never dump chart JSON.
				 */
				if (!bodyHtml) {
					bodyHtml = `<pre style="white-space: pre-wrap; direction: rtl; text-align: right;">${escapeHtml(
						textContent
					)}</pre>`;
				}

				const htmlDocument =
					wrapExportHtmlDocument({
						bodyHtml,
						title:
							report?.title ||
							'Assistant response'
					});

				const isWord = format === 'doc';
				const blob = new Blob(
					[htmlDocument],
					{
						type: isWord
							? 'application/msword;charset=utf-8'
							: 'text/html;charset=utf-8'
					}
				);

				const chatId = chatIds[currentChatIndex] || 'chat';
				const base = sanitizeFilename(`QA_${chatId}_${timestamp()}`);

				downloadBlob(
					blob,
					`${base}.${
						isWord ? 'doc' : 'html'
					}`
				);

				onToggleExportMenu(index);
				return;
			} catch (error) {
				console.error(
					'Failed to export structured output with chart images. Falling back to text export:',
					error
				);

				/* Continue below with the normal text-only exporter. */
			}
		}

		/*
		 * TXT / Markdown / JSON are intentionally text/data-only.
		 * If image export above failed, HTML/DOC also arrive here and are
		 * exported as plain readable content by the existing utility.
		 */
		const exportContent =
			format === 'json' && hasStructuredOutput
				? safeJsonStringify({
						content: textContent,
						report: hasReport
							? report
							: null
					  })
				: textContent;

		const exportMessages = messages.map(
			(message, messageIndex) =>
				messageIndex === index
					? {
							...message,
							content: exportContent,
							/*
							 * Do not let downstream exporters accidentally serialize
							 * the chart payload when this is a text-only fallback.
							 */
							charts: [],
							result: message.result
								? {
										...message.result,
										charts: []
								  }
								: message.result
					  }
					: message
		);

		handleExport(
			index,
			format,
			exportMessages,
			chatIds,
			currentChatIndex
		);

		onToggleExportMenu(index);
	};

	const [
		showCopySnackbar,
		setShowCopySnackbar
	] = useState(false);

	const copyTextOnly = async (text) => {
		if (navigator.clipboard?.writeText) {
			await navigator.clipboard.writeText(
				text || ''
			);
			return;
		}

		const textarea =
			document.createElement('textarea');

		textarea.value = text || '';
		textarea.style.position = 'fixed';
		textarea.style.opacity = '0';
		document.body.appendChild(textarea);
		textarea.focus();
		textarea.select();
		document.execCommand('copy');
		document.body.removeChild(textarea);
	};

	const copyText = async (text) => {
		try {
			let copiedWithCharts = false;

			/*
			 * Rich clipboard attempt for structured chart responses:
			 *   - text/plain => readable report text
			 *   - text/html  => report with embedded PNG chart images
			 *   - image/png  => all charts combined vertically into one PNG
			 *
			 * Different target applications choose the MIME type they support.
			 */
			if (
				hasStructuredOutput &&
				hasCharts &&
				navigator.clipboard?.write &&
				typeof window.ClipboardItem !== 'undefined'
			) {
				try {
					const structuredReportElement =
						messageElementRef.current?.querySelector(
							'.structured-report'
						);

					if (structuredReportElement) {
						const chartCaptures =
							await captureChartImages(
								structuredReportElement
							);

						if (chartCaptures.length > 0) {
							const clipboardData = {
								'text/plain': new Blob(
									[text || ''],
									{
										type: 'text/plain'
									}
								)
							};

							const richHtml =
								buildStructuredHtmlWithImages({
									structuredReportElement,
									chartCaptures
								});

							if (richHtml) {
								clipboardData['text/html'] =
									new Blob(
										[richHtml],
										{
											type: 'text/html'
										}
									);
							}

							const combinedChartsBlob =
								await combineChartCapturesToBlob(
									chartCaptures
								);

							if (combinedChartsBlob) {
								clipboardData['image/png'] =
									combinedChartsBlob;
							}

							await navigator.clipboard.write([
								new window.ClipboardItem(
									clipboardData
								)
							]);

							copiedWithCharts = true;
						}
					}
				} catch (error) {
					console.warn(
						'Rich copy with chart images is not supported here. Falling back to text only:',
						error
					);
				}
			}

			if (!copiedWithCharts) {
				await copyTextOnly(text);
			}

			setShowCopySnackbar(true);
		} catch (error) {
			console.error(
				'Failed to copy response:',
				error
			);
		}
	};

	const { isOpen } = useTour();

	return (
		<div
			className={
				msg.role === 'assistant'
					? 'assistant-msg-wrapper'
					: 'user-msg-wrapper'
			}
		>
			{showCopySnackbar && (
				<Snackbar
					message="کپی شد!"
					type="success"
					duration={2000}
					onClose={() => {
						setShowCopySnackbar(false);
					}}
				/>
			)}

			<div className="message-wrapper">
				<div
					className={`message ${msg.role}`}
					ref={messageElementRef}
				>
					{isEditing ? (
						<>
							<textarea
								className="message-edit-input"
								ref={editTextareaRef}
								value={editingText}
								onChange={(event) => {
									setEditingText(
										event.target.value
									);
								}}
								onKeyDown={(event) => {
									if (
										event.key === 'Enter' &&
										!event.shiftKey
									) {
										event.preventDefault();

										if (
											editingText?.trim()
										) {
											saveEdit(index);
										}
									}
								}}
								autoFocus
							/>

							<br />

							<div className="edit-actions">
								<button
									className="cancel-edit-btn"
									onClick={() => onEdit(-1)}
								>
									لغو
								</button>

								<button
									disabled={
										!editingText?.trim()
									}
									onClick={() => {
										if (
											editingText?.trim()
										) {
											saveEdit(index);
										}
									}}
								>
									ارسال
								</button>
							</div>
						</>
					) : hasStructuredOutput ? (
						<StructuredReport
							report={
								hasReport
									? report
									: null
							}
							charts={charts}
							fallbackContent={
								formattedContent
							}
							geoJsonBaseUrl="/iran-geojson"
						/>
					) : (
						/*
						 * Original behavior:
						 * no report + no chart.
						 */
						<MarkdownContent>
							{formattedContent}
						</MarkdownContent>
					)}

					{/* Attachments remain unchanged */}
					{msg.attachments?.length > 0 && (
						<div className="message-attachments">
							{msg.attachments.map(
								(attachment, attachmentIndex) => (
									<a
										key={
											attachment.id ||
											attachment.url ||
											attachmentIndex
										}
										className="message-attachment"
										href={attachment.url}
										download={attachment.name}
										target="_blank"
										rel="noreferrer"
										title={attachment.name}
									>
										{attachment.isImage ? (
											<img
												src={attachment.url}
												alt={attachment.name}
											/>
										) : (
											<div className="file-icon">
												{attachment.name
													?.split('.')
													.pop()
													?.toUpperCase() ||
													'FILE'}
											</div>
										)}

										<div className="file-name">
											{attachment.name}
										</div>
									</a>
								)
							)}
						</div>
					)}

					{/* References remain unchanged */}
					{msg.role === 'assistant' &&
					msg.references?.length > 0 ? (

					<div className="references">

					{
					msg.references.map((reference, referenceIndex) => {


						// New retriever reference
						if (typeof reference === "object") {

							return (
								<div
									className="reference-item"
									key={reference.id}
								>

									<a
										href={reference.url}
										target="_blank"
										rel="noreferrer"
									>
										[{reference.id}] {reference.title}
									</a>

								</div>
							);
						}


						// Old document reference
						return (
							<a
								href={
									`${API_BASE_FILES}/` +
									`${reference}.docx`
								}
								key={`${reference}-${referenceIndex}`}
								target="_blank"
								rel="noreferrer"
							>
								{reference}
							</a>
						);

					})

					}

					</div>

					) : null}

					{/* =====================================================
					    Keep your existing message action buttons here.
					    Only the surrounding logic is shown below.
					===================================================== */}

					<div
						className="message-actions-bar"
						style={{
							opacity: isOpen ? '1' : ''
						}}
					>
						{!isEditing ? (
							msg.role === 'user' ? (
								<>
									<button
										onClick={() => onEdit(index)}
										title="ویرایش"
										type="button"
									>
										<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <g clipPath="url(#clip0_1197_1548)">
                                                <path d="M9.14203 10.1725C8.98748 10.3266 8.79935 10.4429 8.59237 10.5122L6.60359 11.1751C6.46023 11.223 6.31008 11.2474 6.15894 11.2473C5.78328 11.2473 5.43012 11.101 5.16453 10.8354C4.78544 10.4562 4.65531 9.90484 4.82484 9.39631L5.48778 7.40763C5.55702 7.20061 5.67329 7.01246 5.82747 6.85794L10.1229 2.5625H1.71875C0.771031 2.5625 0 3.33353 0 4.28125V14.2812C0 15.229 0.771031 16 1.71875 16H11.7188C12.6665 16 13.4375 15.229 13.4375 14.2812V5.87706L9.14203 10.1725Z" fill="#014A3F" />
                                                <path d="M6.49039 7.52088C6.43893 7.57234 6.40017 7.63507 6.37717 7.7041L5.71423 9.69282C5.68671 9.7754 5.68273 9.86402 5.70273 9.94873C5.72273 10.0335 5.76593 10.1109 5.82748 10.1725C5.88903 10.234 5.9665 10.2772 6.05122 10.2972C6.13594 10.3172 6.22455 10.3132 6.30714 10.2857L8.29589 9.62282C8.36493 9.59983 8.42766 9.56106 8.4791 9.5096L14.3349 3.65388L12.3461 1.66516L6.49039 7.52088ZM14.8873 0.22882C14.5822 -0.0762734 14.0875 -0.0762734 13.7824 0.22882L13.009 1.00223L14.9978 2.99098L15.7712 2.21757C16.0763 1.91248 16.0763 1.41782 15.7712 1.11273L14.8873 0.22882Z" fill="#014A3F" />
                                            </g>
                                            <defs>
                                                <clipPath id="clip0_1197_1548">
                                                    <rect width="16" height="16" fill="white" />
                                                </clipPath>
                                            </defs>
									    </svg>
									</button>

									<button
										onClick={() =>
											copyText(getCopyableContent())
										}
										title="کپی"
										type="button"
									>
										<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
										    <path d="M5.83337 13C4.27137 13 3 11.7286 3 10.1666V3.33337H1.83337C0.822031 3.33337 0 4.15525 0 5.16662V14.1666C0 15.178 0.822031 16 1.83337 16H10.1666C11.178 16 12 15.178 12 14.1666V13H5.83337Z" fill="#014A3F" />
										    <path d="M14.6666 1.83337C14.6666 0.820688 13.8459 0 12.8334 0H5.83337C4.82069 0 4 0.820688 4 1.83337V10.1666C4 11.1793 4.82069 12 5.83337 12H12.8334C13.8459 12 14.6666 11.1793 14.6666 10.1666V1.83337Z" fill="#014A3F" />
									    </svg>
									</button>
								</>
							) : (
								<>
									<div className="export-wrapper">
										<button
											className="export-btn main"
											title="خروجی گرفتن"
											type="button"
											onClick={() =>
												onToggleExportMenu(index)
											}
										>
											<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
											    <path fillRule="evenodd" clipRule="evenodd" d="M9.2675 0H1C0.734784 0 0.48043 0.105357 0.292893 0.292893C0.105357 0.48043 0 0.734784 0 1L0 13C0 14.6567 1.3425 16 3 16H15C15.2652 16 15.5196 15.8946 15.7071 15.7071C15.8946 15.5196 16 15.2652 16 15V6.732C15.7485 6.87742 15.4684 6.96627 15.179 6.99236C14.8897 7.01845 14.5982 6.98114 14.3247 6.88305C14.0513 6.78495 13.8025 6.62842 13.5957 6.42437C13.389 6.22031 13.2292 5.97362 13.1275 5.7015L9.415 9.41425C9.03947 9.78907 8.53057 9.99958 8 9.99958C7.46943 9.99958 6.96053 9.78907 6.585 9.41425C6.21039 9.03891 6 8.53029 6 8C6 7.46971 6.21039 6.96109 6.585 6.58575L10.2975 2.873C10.0257 2.77085 9.77934 2.61077 9.57559 2.40388C9.37183 2.19699 9.21552 1.94824 9.11753 1.6749C9.01953 1.40155 8.98219 1.11016 9.00808 0.820935C9.03398 0.531712 9.1225 0.251587 9.2675 0ZM12.585 2L7.2925 7.293C6.9025 7.68325 6.9025 8.31675 7.2925 8.707C7.6825 9.09725 8.3175 9.09725 8.7075 8.707L14 3.41425V5C14 5.26522 14.1054 5.51957 14.2929 5.70711C14.4804 5.89464 14.7348 6 15 6C15.2652 6 15.5196 5.89464 15.7071 5.70711C15.8946 5.51957 16 5.26522 16 5V1C16 0.734784 15.8946 0.48043 15.7071 0.292893C15.5196 0.105357 15.2652 0 15 0L11 0C10.7348 0 10.4804 0.105357 10.2929 0.292893C10.1054 0.48043 10 0.734784 10 1C10 1.26522 10.1054 1.51957 10.2929 1.70711C10.4804 1.89464 10.7348 2 11 2H12.585Z" fill="#014A3F" />
										    </svg>
										</button>

										{exportMenuOpen === index && (
											<div
												className="export-menu"
												style={exportMenuStyle}
												ref={exportMenuRef}
											>
												<button
													type="button"
													onClick={() =>
														handleExportClick(
															'txt'
														)
													}
												>
                                                    <span>
                                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <path fillRule="evenodd" clipRule="evenodd" d="M2.81626 0.310547H8.88408V4.4222C8.88408 4.80605 9.1978 5.11977 9.58166 5.11977H13.8631V15.0187C13.8631 15.4026 13.5494 15.7163 13.1655 15.7163H2.81257C2.42872 15.7163 2.11499 15.4026 2.11499 15.0187V1.00812C2.11499 0.624272 2.42872 0.310547 2.81257 0.310547H2.81626ZM6.23033 7.71816H5.21903V10.645H4.4624V7.71816H3.45478V7.10178H6.23402V7.71816H6.23033ZM9.55213 10.645H8.65524L8.01672 9.46764C7.99458 9.42335 7.97243 9.34953 7.94659 9.23511H7.93921C7.92814 9.29048 7.89861 9.36799 7.85801 9.47502L7.21949 10.645H6.31522L7.45201 8.86972L6.41118 7.09809H7.33022L7.85801 8.18321C7.9023 8.27179 7.93552 8.36775 7.96874 8.47479H7.97612C8.00934 8.37883 8.04625 8.27548 8.09423 8.16845L8.67739 7.0944H9.5226L8.45225 8.85126L9.55213 10.6377V10.645ZM12.5307 7.71816H11.5194V10.645H10.7627V7.71816H9.75513V7.10178H12.5344V7.71816H12.5307ZM13.8631 4.77283H9.88431C9.54106 4.77283 9.26055 4.49232 9.26055 4.14907V0.310547L13.8631 4.77283Z" fill="#014A3F" />
                                                        </svg>
                                                    </span>
                                                    <span>خروجی جواب به صورت text</span>
												</button>

												<button
													type="button"
													onClick={() =>
														handleExportClick(
															'doc'
														)
													}
												>
													<span>
                                                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <path fillRule="evenodd" clipRule="evenodd" d="M1.99617 6.49063H1.62164C1.00112 6.49063 0.498047 6.9937 0.498047 7.61423V12.9825C0.498047 13.3962 0.833388 13.7316 1.24711 13.7316H12.7328C12.9314 13.7315 13.1219 13.6526 13.2624 13.5121C13.4029 13.3716 13.4818 13.1811 13.4818 12.9825V8.98753C13.4818 8.78887 13.4029 8.59835 13.2624 8.45788C13.122 8.31741 12.9314 8.23848 12.7328 8.23847H1.62116C1.53925 8.23847 1.45813 8.22235 1.38245 8.191C1.30677 8.15966 1.238 8.11371 1.18008 8.05579C1.12216 7.99786 1.07621 7.9291 1.04487 7.85342C1.01352 7.77774 0.997395 7.69662 0.997403 7.61471V7.61423C0.997403 7.26948 1.27689 6.98999 1.62164 6.98999H1.99617V7.73905H12.2334V3.74408H10.2359C10.1375 3.7441 10.0401 3.72474 9.9492 3.6871C9.85831 3.64946 9.77573 3.59428 9.70617 3.52472C9.63661 3.45516 9.58144 3.37258 9.5438 3.28169C9.50616 3.1908 9.48679 3.09339 9.48681 2.99502V0.248413H2.74524C2.54658 0.248429 2.35606 0.327353 2.21559 0.467826C2.07511 0.6083 1.99619 0.798818 1.99617 0.997477V6.49063ZM8.39797 9.51169H8.25529C7.56579 9.51169 7.00687 10.0706 7.00687 10.7601V11.2595C7.00687 11.949 7.56579 12.5079 8.25529 12.5079H8.39797C8.61608 12.508 8.8296 12.4453 9.0131 12.3274C9.19661 12.2095 9.34238 12.0414 9.43306 11.843C9.45889 11.7866 9.46398 11.7759 9.48058 11.7391C9.50802 11.6816 9.51218 11.6157 9.49219 11.5552C9.4722 11.4947 9.42961 11.4442 9.37331 11.4144C9.21679 11.3357 9.07477 11.4229 9.02641 11.5315C9.01172 11.5644 8.97889 11.6354 8.97889 11.6354C8.92799 11.7467 8.84618 11.8411 8.7432 11.9072C8.64021 11.9734 8.52038 12.0086 8.39797 12.0086H8.25529C7.84157 12.0086 7.50623 11.6732 7.50623 11.2595V10.7601C7.50623 10.3465 7.84157 10.0111 8.25529 10.0111H8.39797C8.52038 10.0111 8.6402 10.0462 8.74319 10.1124C8.84617 10.1785 8.92798 10.2729 8.97889 10.3842C8.97889 10.3842 9.00933 10.4524 9.02641 10.4882C9.10143 10.6454 9.27887 10.6678 9.39099 10.5948C9.46979 10.5436 9.54422 10.4197 9.48058 10.2805L9.43306 10.1766C9.34238 9.97826 9.19661 9.81013 9.0131 9.69224C8.8296 9.57436 8.61608 9.51169 8.39797 9.51169ZM5.50227 9.48689C4.82991 9.48689 4.25379 10.1446 4.25379 10.985C4.25379 11.8255 4.82991 12.4831 5.50227 12.4831C6.17463 12.4831 6.75069 11.8255 6.75069 10.985C6.75069 10.1446 6.17463 9.48689 5.50227 9.48689ZM3.97298 10.7353C3.97298 10.4042 3.84145 10.0867 3.60733 9.85254C3.37321 9.61841 3.05567 9.48689 2.72456 9.48689H1.85068C1.81788 9.48688 1.78541 9.49333 1.75511 9.50588C1.72481 9.51842 1.69728 9.53682 1.67409 9.56001C1.6509 9.5832 1.63251 9.61073 1.61996 9.64102C1.60741 9.67132 1.60096 9.7038 1.60097 9.73659V12.2334C1.60096 12.2662 1.60741 12.2987 1.61996 12.329C1.63251 12.3593 1.6509 12.3868 1.67409 12.41C1.69728 12.4332 1.72481 12.4516 1.75511 12.4641C1.78541 12.4767 1.81788 12.4831 1.85068 12.4831H2.72456C3.05567 12.4831 3.37321 12.3516 3.60733 12.1175C3.84145 11.8834 3.97298 11.5658 3.97298 11.2347V10.7353ZM11.3686 10.985L12.3171 9.90097C12.3607 9.85112 12.3827 9.78601 12.3783 9.71996C12.3739 9.6539 12.3434 9.59229 12.2936 9.54867C12.2438 9.5051 12.1787 9.48311 12.1126 9.48751C12.0465 9.49191 11.9849 9.52236 11.9413 9.57216L11.0368 10.6059L10.1323 9.57216C10.0416 9.46843 9.88375 9.45794 9.78002 9.54867C9.67629 9.63939 9.66581 9.7973 9.75653 9.90097L10.705 10.985L9.75653 12.0691C9.66581 12.1727 9.67629 12.3306 9.78002 12.4214C9.88375 12.5121 10.0416 12.5016 10.1323 12.3979L11.0368 11.3642L11.9413 12.3979C11.9849 12.4477 12.0465 12.4781 12.1126 12.4825C12.1787 12.4869 12.2438 12.4649 12.2936 12.4214C12.3434 12.3777 12.3739 12.3161 12.3783 12.2501C12.3827 12.184 12.3607 12.1189 12.3171 12.0691L11.3686 10.985ZM3.47363 10.7353V11.2347C3.47358 11.4334 3.39465 11.6239 3.25418 11.7643C3.11371 11.9048 2.92321 11.9837 2.72456 11.9838H2.10032V9.98624H2.72456C2.92322 9.98626 3.11374 10.0652 3.25421 10.2057C3.39469 10.3461 3.47361 10.5366 3.47363 10.7353ZM5.50227 9.98624C5.93271 9.98624 6.25134 10.4469 6.25134 10.985C6.25134 11.5231 5.93271 11.9838 5.50227 11.9838C5.07183 11.9838 4.75321 11.5231 4.75321 10.985C4.75321 10.4469 5.07183 9.98624 5.50227 9.98624ZM4.24337 6.74034H9.98617C10.124 6.74034 10.2359 6.62846 10.2359 6.49063C10.2359 6.3528 10.124 6.24092 9.98617 6.24092H4.24337C4.10554 6.24092 3.99366 6.3528 3.99366 6.49063C3.99366 6.62846 4.10554 6.74034 4.24337 6.74034ZM4.24337 5.24221H9.98617C10.124 5.24221 10.2359 5.13033 10.2359 4.9925C10.2359 4.85468 10.124 4.7428 9.98617 4.7428H4.24337C4.10554 4.7428 3.99366 4.85468 3.99366 4.9925C3.99366 5.13033 4.10554 5.24221 4.24337 5.24221ZM4.24337 3.74408H7.48933C7.6271 3.74408 7.73898 3.6322 7.73898 3.49438C7.73898 3.35655 7.6271 3.24467 7.48933 3.24467H4.24337C4.10554 3.24467 3.99366 3.35655 3.99366 3.49438C3.99366 3.6322 4.10554 3.74408 4.24337 3.74408ZM9.98617 0.414885V2.99502C9.98615 3.02781 9.9926 3.06029 10.0051 3.09059C10.0177 3.12089 10.0361 3.14842 10.0593 3.1716C10.0825 3.19479 10.11 3.21318 10.1403 3.22571C10.1706 3.23825 10.2031 3.24469 10.2359 3.24467H12.1085L9.98617 0.414885Z" fill="#014A3F" />
                                                        </svg>
                                                    </span>
                                                    <span>خروجی جواب به صورت Doc (Word)</span>
												</button>

												<button
													type="button"
													onClick={() =>
														handleExportClick(
															'md'
														)
													}
												>
													<span>
                                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <path d="M3.3125 16H12.6875C13.4629 16 14.0938 15.3692 14.0938 14.5938V4.6875H10.8125C10.0371 4.6875 9.40625 4.05669 9.40625 3.28125V0H3.3125C2.53706 0 1.90625 0.630813 1.90625 1.40625V14.5938C1.90625 15.3692 2.53706 16 3.3125 16ZM5.1875 6.59375H10.8125C11.0716 6.59375 11.2812 6.80341 11.2812 7.0625C11.2812 7.32159 11.0716 7.53125 10.8125 7.53125H5.1875C4.92841 7.53125 4.71875 7.32159 4.71875 7.0625C4.71875 6.80341 4.92841 6.59375 5.1875 6.59375ZM5.1875 8.46875H10.8125C11.0716 8.46875 11.2812 8.67841 11.2812 8.9375C11.2812 9.19659 11.0716 9.40625 10.8125 9.40625H5.1875C4.92841 9.40625 4.71875 9.19659 4.71875 8.9375C4.71875 8.67841 4.92841 8.46875 5.1875 8.46875ZM5.1875 10.3438H10.8125C11.0716 10.3438 11.2812 10.5534 11.2812 10.8125C11.2812 11.0716 11.0716 11.2812 10.8125 11.2812H5.1875C4.92841 11.2812 4.71875 11.0716 4.71875 10.8125C4.71875 10.5534 4.92841 10.3438 5.1875 10.3438ZM5.1875 12.2188H8.9375C9.19659 12.2188 9.40625 12.4284 9.40625 12.6875C9.40625 12.9466 9.19659 13.1562 8.9375 13.1562H5.1875C4.92841 13.1562 4.71875 12.9466 4.71875 12.6875C4.71875 12.4284 4.92841 12.2188 5.1875 12.2188Z" fill="#014A3F" />
                                                            <path d="M10.8125 3.75H13.8191L10.3438 0.274658V3.28125C10.3438 3.53988 10.5539 3.75 10.8125 3.75Z" fill="#014A3F" />
                                                        </svg>
                                                    </span>
                                                    <span>خروجی جواب به صورت Markdown</span>
												</button>

												<button
													type="button"
													onClick={() =>
														handleExportClick(
															'json'
														)
													}
												>
													<span>
                                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <path d="M8.54864 10.082C8.20397 10.082 8.00464 10.4094 8.00464 10.846C8.00464 11.2867 8.21197 11.5967 8.55131 11.5967C8.89597 11.5967 9.09264 11.27 9.09264 10.8327C9.09264 10.43 8.89864 10.082 8.54864 10.082Z" fill="#014A3F" />
                                                            <path d="M9.33342 1.33398H4.00008C3.64646 1.33398 3.30732 1.47446 3.05727 1.72451C2.80722 1.97456 2.66675 2.3137 2.66675 2.66732V13.334C2.66675 13.6876 2.80722 14.0267 3.05727 14.2768C3.30732 14.5268 3.64646 14.6673 4.00008 14.6673H12.0001C12.3537 14.6673 12.6928 14.5268 12.9429 14.2768C13.1929 14.0267 13.3334 13.6876 13.3334 13.334V5.33398L9.33342 1.33398ZM5.34808 11.1367C5.34808 11.7773 5.04075 12.0007 4.54808 12.0007C4.43075 12.0007 4.27741 11.9813 4.17675 11.9473L4.23408 11.5373C4.30341 11.5607 4.39341 11.5773 4.49475 11.5773C4.70741 11.5773 4.84141 11.4807 4.84141 11.1307V9.71598H5.34875L5.34808 11.1367ZM6.32075 11.9973C6.06408 11.9973 5.81008 11.9307 5.68408 11.8607L5.78741 11.44C5.92341 11.51 6.13475 11.5807 6.35142 11.5807C6.58475 11.5807 6.70741 11.4833 6.70741 11.3373C6.70741 11.1967 6.60141 11.1167 6.33141 11.02C5.95675 10.89 5.71341 10.6827 5.71341 10.356C5.71341 9.97198 6.03408 9.67798 6.56475 9.67798C6.81808 9.67798 7.00408 9.73132 7.13875 9.79198L7.02408 10.202C6.93408 10.1587 6.77408 10.0953 6.55408 10.0953C6.33408 10.0953 6.22675 10.1953 6.22675 10.312C6.22675 10.4553 6.35341 10.5193 6.64475 10.6293C7.04208 10.776 7.22875 10.9827 7.22875 11.3C7.22941 11.6773 6.93808 11.9973 6.32075 11.9973ZM8.52475 12.0007C7.85741 12.0007 7.46741 11.4973 7.46741 10.8567C7.46741 10.182 7.89808 9.67798 8.56208 9.67798C9.25208 9.67798 9.62942 10.1953 9.62942 10.816C9.62875 11.554 9.18208 12.0007 8.52475 12.0007ZM11.8227 11.964H11.2881L10.8074 11.096C10.6636 10.8385 10.5334 10.5737 10.4174 10.3027L10.4067 10.306C10.4207 10.6027 10.4274 10.92 10.4274 11.2873V11.9647H9.96008V9.71598H10.5541L11.0214 10.54C11.1547 10.776 11.2881 11.0567 11.3894 11.31H11.3987C11.3654 11.0133 11.3554 10.71 11.3554 10.3727V9.71598H11.8234V11.964H11.8227ZM9.33342 6.00065H8.66675V2.66732L12.0001 6.00065H9.33342Z" fill="#014A3F" />
                                                        </svg>
                                                    </span>
												<span>خروجی جواب به صورت JSON</span>
												</button>

												<button
													type="button"
													onClick={() =>
														handleExportClick(
															'html'
														)
													}
												>
                                                    <span>
                                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <path fillRule="evenodd" clipRule="evenodd" d="M2.81626 0.310547H8.88408V4.4222C8.88408 4.80605 9.1978 5.11977 9.58166 5.11977H13.8631V15.0187C13.8631 15.4026 13.5494 15.7163 13.1655 15.7163H2.81257C2.42872 15.7163 2.11499 15.4026 2.11499 15.0187V1.00812C2.11499 0.624272 2.42872 0.310547 2.81257 0.310547H2.81626ZM6.23033 7.71816H5.21903V10.645H4.4624V7.71816H3.45478V7.10178H6.23402V7.71816H6.23033ZM9.55213 10.645H8.65524L8.01672 9.46764C7.99458 9.42335 7.97243 9.34953 7.94659 9.23511H7.93921C7.92814 9.29048 7.89861 9.36799 7.85801 9.47502L7.21949 10.645H6.31522L7.45201 8.86972L6.41118 7.09809H7.33022L7.85801 8.18321C7.9023 8.27179 7.93552 8.36775 7.96874 8.47479H7.97612C8.00934 8.37883 8.04625 8.27548 8.09423 8.16845L8.67739 7.0944H9.5226L8.45225 8.85126L9.55213 10.6377V10.645ZM12.5307 7.71816H11.5194V10.645H10.7627V7.71816H9.75513V7.10178H12.5344V7.71816H12.5307ZM13.8631 4.77283H9.88431C9.54106 4.77283 9.26055 4.49232 9.26055 4.14907V0.310547L13.8631 4.77283Z" fill="#014A3F" />
                                                        </svg>
                                                    </span>
                                                    <span>خروجی جواب به صورت HTML</span>
												</button>
											</div>
										)}
									</div>

									<button
										className="copy-button"
										type="button"
										onClick={() =>
											copyText(getCopyableContent())
										}
										title="کپی"
									>
										<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
										    <path d="M5.83337 13C4.27137 13 3 11.7286 3 10.1666V3.33337H1.83337C0.822031 3.33337 0 4.15525 0 5.16662V14.1666C0 15.178 0.822031 16 1.83337 16H10.1666C11.178 16 12 15.178 12 14.1666V13H5.83337Z" fill="#014A3F" />
										    <path d="M14.6666 1.83337C14.6666 0.820688 13.8459 0 12.8334 0H5.83337C4.82069 0 4 0.820688 4 1.83337V10.1666C4 11.1793 4.82069 12 5.83337 12H12.8334C13.8459 12 14.6666 11.1793 14.6666 10.1666V1.83337Z" fill="#014A3F" />
									    </svg>
									</button>

									<button
										type="button"
										className={
											`dislike ${
												messages[index]
													?.feedback ===
												'disliked'
													? 'activated'
													: ''
											}`
										}
										onClick={() =>
											onFeedback(
												index,
												'disliked'
											)
										}
										title="نپسندیدم"
									>
										<svg width="16" height="16" ><use href="#icon-dislike" /></svg>
									</button>

									<button
										type="button"
										className={
											`like ${
												messages[index]
													?.feedback ===
												'liked'
													? 'activated'
													: ''
											}`
										}
										onClick={() =>
											onFeedback(
												index,
												'liked'
											)
										}
										title="پسندیدم"
									>
										<svg width="16" height="16"><use href="#icon-like" /></svg>
									</button>
								</>
							)
						) : null}
					</div>
				</div>

				<div className="time-wrapper">
					{/* {msg.time} */}
				</div>
			</div>
		</div>
	);
};

Message.propTypes = {
	msg: PropTypes.shape({
		role: PropTypes.string.isRequired,
		content: PropTypes.string,
		report: PropTypes.object,
		charts: PropTypes.array,
		result: PropTypes.shape({
			report: PropTypes.object,
			charts: PropTypes.array
		}),
		attachments: PropTypes.array,
		references: PropTypes.array,
		feedback: PropTypes.string
	}).isRequired,

	index: PropTypes.number.isRequired,
	onEdit: PropTypes.func.isRequired,
	onFeedback: PropTypes.func.isRequired,
	exportMenuOpen: PropTypes.oneOfType([
		PropTypes.number,
		PropTypes.bool
	]),
	onToggleExportMenu: PropTypes.func.isRequired,
	exportMenuStyle: PropTypes.object,
	messages: PropTypes.array.isRequired,
	chatIds: PropTypes.array,
	currentChatIndex: PropTypes.number,
	isEditing: PropTypes.bool,
	editingText: PropTypes.string,
	setEditingText: PropTypes.func.isRequired,
	saveEdit: PropTypes.func.isRequired
};

export default Message;